import { supabase } from "@/integrations/supabase/client";
import { studentLoginCodeService } from "@/services/teacherInviteService";

const DEMO_WORD1 = "demo";
const DEMO_PASSWORD = "Demo123!";
const DEMO_FIRST_NAME = "Demo";
const DEMO_LAST_NAME = "Účet";

/** Matches CreateClassDialog / DB helper: first 8 characters of class UUID string. */
export function getDemoWord2ForClass(classId: string): string {
  return classId.substring(0, 8);
}

/**
 * Demo for a class is identified by login words demo + class id prefix, not only
 * is_test_student (avoids treating stray flagged accounts as the class demo).
 */
async function findCanonicalDemoStudentIdInClass(classId: string): Promise<string | null> {
  const word2 = getDemoWord2ForClass(classId);
  const { data: rows, error } = await supabase
    .from("class_students")
    .select("student_id")
    .eq("class_id", classId);

  if (error) {
    console.error("[demoStudentService.findCanonicalDemoStudentIdInClass] class_students", error);
    throw error;
  }
  if (!rows?.length) return null;

  const memberIds = rows.map((r) => r.student_id);
  const { data: profiles, error: profileError } = await supabase
    .from("profiles")
    .select("id")
    .in("id", memberIds)
    .eq("is_test_student", true);

  if (profileError) {
    console.error("[demoStudentService.findCanonicalDemoStudentIdInClass] profiles", profileError);
    throw profileError;
  }

  const testIds = profiles?.map((p) => p.id) ?? [];
  if (testIds.length === 0) return null;

  const { data: wordRows, error: studentsError } = await supabase
    .from("students")
    .select("id")
    .in("id", testIds)
    .eq("word1", DEMO_WORD1)
    .eq("word2", word2);

  if (studentsError) {
    console.error("[demoStudentService.findCanonicalDemoStudentIdInClass] students", studentsError);
    throw studentsError;
  }

  if (!wordRows?.length) return null;
  if (wordRows.length > 1) {
    console.error(
      "[demoStudentService.findCanonicalDemoStudentIdInClass] multiple rows for canonical word pair",
      { classId, count: wordRows.length }
    );
  }
  return wordRows[0].id;
}

/**
 * Demo login words are unique per class (word2 = class id prefix). If creation
 * partially failed, the student row may exist without a class_students row.
 */
async function findOrphanDemoStudentForClass(classId: string): Promise<string | null> {
  const word2 = getDemoWord2ForClass(classId);
  const { data: studentRow, error } = await supabase
    .from("students")
    .select("id")
    .eq("word1", DEMO_WORD1)
    .eq("word2", word2)
    .maybeSingle();

  if (error) {
    console.error("[demoStudentService.findOrphanDemoStudentForClass] students", error);
    throw error;
  }
  if (!studentRow?.id) return null;

  const { data: profile, error: profileError } = await supabase
    .from("profiles")
    .select("is_test_student")
    .eq("id", studentRow.id)
    .maybeSingle();

  if (profileError) {
    console.error("[demoStudentService.findOrphanDemoStudentForClass] profile", profileError);
    throw profileError;
  }
  if (!profile?.is_test_student) return null;
  return studentRow.id;
}

async function linkStudentToClass(classId: string, studentId: string): Promise<void> {
  const { error } = await supabase.from("class_students").insert({
    class_id: classId,
    student_id: studentId,
  });

  if (error) {
    if (error.code === "23505") {
      return;
    }
    console.error("[demoStudentService.linkStudentToClass]", error);
    throw error;
  }
}

export type EnsureDemoStudentResult = {
  studentId: string;
  created: boolean;
};

/**
 * Ensures the class has a demo (test) student: creates credentials via edge function
 * if missing, or links an existing demo row for this class word pair.
 */
export async function ensureDemoStudentForClass(classId: string): Promise<EnsureDemoStudentResult> {
  const inClass = await findCanonicalDemoStudentIdInClass(classId);
  if (inClass) {
    return { studentId: inClass, created: false };
  }

  const orphanId = await findOrphanDemoStudentForClass(classId);
  if (orphanId) {
    await linkStudentToClass(classId, orphanId);
    return { studentId: orphanId, created: false };
  }

  const result = await studentLoginCodeService.upsertStudentCredentials(
    DEMO_WORD1,
    getDemoWord2ForClass(classId),
    DEMO_PASSWORD,
    DEMO_FIRST_NAME,
    DEMO_LAST_NAME,
    false,
    true
  );

  await linkStudentToClass(classId, result.studentId);
  return { studentId: result.studentId, created: result.created };
}
